import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaBrain,
  FaChartLine,
  FaCubes,
  FaDatabase,
  FaLayerGroup,
  FaShoppingBasket,
  FaTachometerAlt,
} from "react-icons/fa";
import FloatingNavbar from "../components/FloatingNavbar";
import PremiumFooter from "../components/PremiumFooter";
import "./MarketingPages.css";

const features = [
  { icon: <FaTachometerAlt />, title: "Premium dashboard", desc: "A clean command center for high-level business decisions." },
  { icon: <FaChartLine />, title: "Analytics cards", desc: "Metrics are presented in focused, glossy cards for quick reading." },
  { icon: <FaDatabase />, title: "Inventory system", desc: "Stock visibility, movement, and stock health stay clear in one place." },
  { icon: <FaBrain />, title: "AI insights", desc: "Smart cues surface patterns and help teams make better decisions faster." },
  { icon: <FaShoppingBasket />, title: "Order management", desc: "Orders move through the workflow with structured clarity." },
  { icon: <FaLayerGroup />, title: "Unified tools", desc: "A connected toolkit for inventory, analytics, and growth planning." },
];

const stats = [
  { value: "18.2%", label: "Performance pulse" },
  { value: "92%", label: "Inventory confidence" },
  { value: "4.8x", label: "Faster review" },
  { value: "1", label: "Unified dashboard" },
];

const tools = [
  "Live inventory snapshots",
  "Operational trend cards",
  "Sales and margin signals",
  "Multi-team coordination",
  "Quick-access workflow widgets",
  "Growth-friendly layout blocks",
];

export default function FeaturesPage() {
  return (
    <main className="marketing-page">
      <div className="marketing-shell">
        <FloatingNavbar />
        <section className="hero-grid">
          <div>
            <span className="section-label">Features</span>
            <h1>Everything your business needs, shown with premium clarity.</h1>
            <p>
              From analytics cards to inventory visibility and AI-guided cues, the feature set is designed to feel
              modern, powerful, and easy to scan.
            </p>
            <div className="hero-actions">
              <Link className="button-link button-link--accent" to="/workflow">
                See Workflow <FaArrowRight />
              </Link>
              <Link className="button-link" to="/about">
                About the Company
              </Link>
            </div>
          </div>

          <div className="glass-card preview-shell">
            <div className="preview-top">
              <div className="preview-dots"><span /><span /><span /></div>
              <span className="section-label" style={{ margin: 0 }}>Dashboard Preview</span>
            </div>
            <div className="preview-bars">
              <span style={{ height: "44%" }} />
              <span style={{ height: "58%" }} />
              <span style={{ height: "50%" }} />
              <span style={{ height: "78%" }} />
              <span style={{ height: "68%" }} />
              <span style={{ height: "88%" }} />
            </div>
            <div className="preview-curve" />
          </div>
        </section>

        <section className="section-block">
          <div className="section-copy">
            <span className="section-label">Feature grid</span>
            <h2>Interactive hover cards keep the page premium and alive.</h2>
          </div>
          <div className="feature-grid">
            {features.map((feature) => (
              <article className="feature-card" key={feature.title}>
                <div className="card-icon">{feature.icon}</div>
                <div className="card-copy">
                  <h3>{feature.title}</h3>
                  <p>{feature.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="section-block">
          <div className="section-copy">
            <span className="section-label">Analytics</span>
            <h2>Elegant cards surface important business signals quickly.</h2>
          </div>
          <div className="stats-grid">
            {stats.map((item) => (
              <article className="metric-card" key={item.label}>
                <strong>{item.value}</strong>
                <div className="metric-copy">
                  <span>{item.label}</span>
                  <p>Presented with a clean, light premium layout.</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="section-block">
          <div className="dual-grid">
            <article className="panel-card">
              <span className="section-label">Inventory system</span>
              <h3 style={{ marginTop: 16 }}>Inventory that stays readable under load.</h3>
              <p>Stock visibility, movement, and replenishment cues stay easy to understand.</p>
              <div className="preview-curve" style={{ marginTop: 16 }} />
            </article>
            <article className="panel-card">
              <span className="section-label">AI insights</span>
              <h3 style={{ marginTop: 16 }}>Smarter cues without visual clutter.</h3>
              <p>AI-generated summaries and nudges help teams move with confidence.</p>
              <div className="preview-curve" style={{ marginTop: 16 }} />
            </article>
          </div>
        </section>

        <section className="section-block">
          <div className="section-copy">
            <span className="section-label">Business tools</span>
            <h2>A grid of practical tools for daily operations and growth.</h2>
          </div>
          <div className="tools-grid">
            {tools.map((tool) => (
              <article className="help-card" key={tool}>
                <div className="card-icon"><FaCubes /></div>
                <strong>{tool}</strong>
                <p>Built to support premium, focused, high-output workflows.</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section-block">
          <div className="section-copy">
            <span className="section-label">Dashboard preview</span>
            <h2>Polished layout blocks show the business in a better light.</h2>
          </div>
          <div className="dual-grid">
            <article className="panel-card">
              <h3>Sales trend overview</h3>
              <p>Clean bars and soft gradients keep the preview premium and readable.</p>
              <div className="preview-bars" style={{ marginTop: 16, height: 160 }}>
                <span style={{ height: "36%" }} />
                <span style={{ height: "56%" }} />
                <span style={{ height: "48%" }} />
                <span style={{ height: "74%" }} />
                <span style={{ height: "66%" }} />
                <span style={{ height: "90%" }} />
              </div>
            </article>
            <article className="panel-card">
              <h3>Operational snapshot</h3>
              <p>Inventory, analytics, and workflows live together in one elegant picture.</p>
              <div className="preview-curve" style={{ marginTop: 16 }} />
              <div className="dashboard-grid" style={{ marginTop: 16 }}>
                <div className="metric-card"><strong>Ready</strong><span>System health</span></div>
                <div className="metric-card"><strong>Live</strong><span>Stock sync</span></div>
              </div>
            </article>
          </div>
        </section>

        <PremiumFooter />
      </div>
    </main>
  );
}
